import { ComponentType } from '@angular/cdk/overlay';
import { DomPortal, PortalModule } from '@angular/cdk/portal';

import {
  Component,
  ContentChild,
  Directive,
  ElementRef,
  inject,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { PmButtonComponent, PmTooltipDirective } from '@libs/ui';
import { LucideInfo } from '@lucide/angular';
import { WidgetDefaultSelection } from '../../types/types';

@Component({
  selector: 'pm-widget-fullscreen-dialog',
  template: `
    <div>
      Hi
      @if (hasLoaded) {
        <ng-template [cdkPortalOutlet]="domPortal"></ng-template>
      }
      <ng-template #errorPortal>Something went wrong</ng-template>
    </div>
  `,
  imports: [PortalModule],
})
export class WidgetFullscreenDialogComponent {
  @Input({ required: true }) widgetRef: ElementRef<HTMLElement>;
  @ViewChild('errorPortal') errorPortal: TemplateRef<HTMLElement>;

  domPortal:
    | DomPortal<TemplateRef<HTMLElement> | ElementRef<HTMLElement>>
    | undefined;
  hasLoaded = false;

  // TODO this is the right way to do it - currently this is not working because the ref is always undefined
  /* ngAfterViewInit() {
     if (typeof this.widgetRef !== 'undefined') {
      this.domPortal = new DomPortal(this.widgetRef);
    } else {
      this.domPortal = new DomPortal(this.errorPortal);
    }
    console.log(this.widgetRef);
  } */
}

@Directive({
  selector: '[pmWidgetTitle]',
  standalone: true,
})
export class WidgetTitleDirective {
  @Input() infoTooltipText: string;
  readonly templateRef: TemplateRef<string> = inject(TemplateRef);
}

@Directive({
  selector: '[pmWidgetHeader]',
  standalone: true,
})
export class WidgetHeaderDirective {}

@Directive({
  selector: '[pmWidgetContent]',
  standalone: true,
})
export class WidgetContentDirective {}

@Directive({
  selector: '[pmWidgetActions]',
  standalone: true,
})
export class WidgetActionsDirective {}

@Component({
  selector: 'pm-widget-base-wrapper',
  templateUrl: './widget-base.component.html',
  imports: [
    NgClass,
    NgTemplateOutlet,
    MatFormFieldModule,
    MatSelectModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    PmTooltipDirective,
    PortalModule,
    PmButtonComponent,
  ],
})
export class WidgetBaseComponent<T> {
  @ContentChild(WidgetTitleDirective)
  title: WidgetTitleDirective;
  @ContentChild(WidgetHeaderDirective, { read: TemplateRef })
  header: TemplateRef<unknown>;
  @ContentChild(WidgetContentDirective, { read: TemplateRef })
  content: TemplateRef<unknown>;
  @ContentChild(WidgetActionsDirective, { read: TemplateRef })
  actions: TemplateRef<unknown>;

  @Input() isLoading = true;

  @Input()
  name?: string;

  protected readonly matDialog = inject(MatDialog);
  protected readonly dialogRef = inject<MatDialogRef<T>>(MatDialogRef, {
    optional: true,
  });

  @Input() isDialogInstance = !!this.dialogRef;

  protected readonly infoIcon = LucideInfo;

  isError = false;

  showFullScreen(
    T: ComponentType<T>,
    filterSelection?: WidgetDefaultSelection,
  ): MatDialogRef<T> | null {
    return this.matDialog.open(T, {
      data: {
        ...filterSelection,
      },
      minHeight: '90vh',
      minWidth: '90vw',
    });
  }

  exitFullScreen(): void {
    if (this.isDialogInstance) {
      this.dialogRef?.close();
    }
  }

  // TODO migrate dialog with separate component to use DOM ref instead, like below
  /* showFullScreen() {
    console.log('got widget ref: ', this.widgetRef, this.copy);
    return this.matDialog.open(WidgetFullscreenDialogComponent, {
      data: {
        widgetRef: this.widgetRef,
      },
    });
  } */
}
